import {
  AttributionMode,
  AudienceList,
  Day,
  Experiment,
  QuietHours,
  RateLimitUnit,
  ScheduleType,
} from 'modules/domains/campaign';
import {
  GRAPHQL_TYPENAMES,
  GraphQLTypeNameData,
  GraphQLWhatsAppHeader,
  InAppDestination,
  InAppTriggerDefinition,
  MESSAGE_FORMAT,
  MessageChannel,
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  Text,
  WhatsAppButtonDefinition,
} from 'modules/graphql/types';

import { TemplateType } from 'constants/templateTypes';
import { Moment } from 'moment';
import { RecurrenceScheduleType } from '../lib/types';
import { WhatsAppSender } from 'modules/sendingPlatforms/types/whatsAppSenders';

export enum WebInAppDisplaySettingsDefinition {
  BottomRight = 'BottomRight',
  Center = 'Center',
  Full = 'Full',
  TopRight = 'TopRight',
}

export enum InterruptionLevel {
  Active = 'Active',
  Critical = 'Critical',
  Passive = 'Passive',
  TimeSensitive = 'TimeSensitive',
}

export type LinkParams = {
  key: string;
  value: string;
};

/** *
 * TODO: These types are not ready to be moved to `src/modules/domains` yet, move them when they are.
 */
export type EmailPrimaryContent = {
  fromEmail: string;
  fromName: string;
  replyTo: string | null;
  ccEmails: string[];
  subject: string;
  preheaderText: string;
  googleAnalyticsCampaignName: string | null;
  inlineCss: boolean;
  linkParams: LinkParams[];
  html: string;
  beeBuilderMetadata: string;
};

export type BgColor = {
  alpha: number;
  hex: string;
};

export type InAppDisplaySettingsDefinition = {
  bgColor?: BgColor;
  shouldAnimate?: boolean;
};

export type InboxMetadata = {
  title: string;
  subtitle: string;
  icon: string;
};

export type WebPushPrimaryContent = {
  title?: string;
  message?: string;
  icon?: string;
  clickAction?: string;
};

export type InAppPrimaryContent = {
  html: string;
  inAppDisplaySettings: InAppDisplaySettingsDefinition;
  payload?: string;
  webInAppDisplaySettings: WebInAppDisplaySettingsDefinition;
  inboxMetadata: InboxMetadata;
  messageFormat: MESSAGE_FORMAT;
  beeBuilderMetadata: string;
  destinations: InAppDestination[];
  trigger: InAppTriggerDefinition;
  expirationDateTime: string;
  expirationDuration: string;
  saveToInbox: boolean;
};

export type SMSPrimaryContent = {
  message: string;
  smsSender: { name: string };
  imageUrl: string;
  trackingDomain: string;
  googleAnalyticsCampaignName: string | null;
  linkParams: LinkParams[];
  verifiedContactCardId?: number;
};

export type PushPrimaryContent = {
  interruptionLevel?: InterruptionLevel;
  relevanceScore: number;
  badge: number;
  contentAvailable: boolean;
  title: string;
  message: string;
  attachmentUrlIos: string;
  attachmentUrlAndroid: string;
  defaultAction: {
    type: string;
    data: string;
  };
  actionButtons: {
    title: string;
  }[];
  sound: string;
  isSilentPush: boolean;
  payload: string;
};

export type EmbeddedPrimaryContent = {
  body?: string;
  elements?: {
    mediaUrl: string;
    buttons?: {
      id: string;
      title?: string;
      action?: {
        type: string;
        data: string;
      };
    }[];
    text?: Text[];
    defaultAction: {
      type: string;
      data: string;
    };
  };
  embeddedContentId?: number;
  listId?: number;
  locale?: {
    name: string;
  };
  payload?: string;
  placementId?: number;
  title?: string;
};

export type WhatsAppPrimaryContent = {
  header: GraphQLWhatsAppHeader;
  message: string;
  buttons?: WhatsAppButtonDefinition[];
  footer?: string;
  whatsAppCategory?: string;
  whatsAppSender?: WhatsAppSender;
  metaApprovalStatus?: MetaApprovalStatusDefinition;
  qualityRating?: MetaQualityRating;
};

export type PrimaryContent =
  | EmailPrimaryContent
  | SMSPrimaryContent
  | InAppPrimaryContent
  | WebPushPrimaryContent
  | PushPrimaryContent
  | EmbeddedPrimaryContent
  | WhatsAppPrimaryContent;

export type TemplateAndContent = {
  name: string;
  createdAt: string;
  updatedAt: string;
  validationFailures: {
    errors: {
      contentId: number;
    }[];
  };
  shortenLinks?: boolean;
  messageType: {
    id: number;
    name: string;
    messageChannel?: MessageChannel;
  };
  dataFeedIds: number[];
  primaryContent: PrimaryContent;
  templateType: TemplateType | null;
};

export type GraphQLTemplateAndContent = TemplateAndContent &
  GraphQLTypeNameData<GRAPHQL_TYPENAMES.TEMPLATE_AND_CONTENT>;

export type DataFeed = {
  id: number;
  name: string;
  projectId: number;
};

export type DataFeedMapper = Record<number, DataFeed>;

export type CampaignEditorFormState = {
  ignoreFrequencyCap?: boolean;
  useQuietHours: boolean;
  customQuietHours?: Partial<QuietHours>;

  useSendTimeOptimization: boolean;
  stoCompletionTime?: string;
  campaignAlertsEnabled?: boolean;

  useRateLimit: boolean;
  rateLimit?: {
    rateLimitValue?: string;
    rateLimitUnit?: RateLimitUnit;
  };
  conversions: {
    useConversions: boolean;
    events: string[];
    mode: AttributionMode;
    periodHours: string;
  };
  experiments: { experiments: Experiment[]; useExperiments: boolean };
  audienceLists: {
    useSendLists: boolean;
    sendLists: AudienceList[];
    useSuppressionLists: boolean;
    suppressionLists: AudienceList[];
  };
  priorityLevel?: number;
}; // we need to type this out to work for our form library versus the read only state of the form.

export type CampaignEditorFormStateKeys = keyof CampaignEditorFormState;

export enum EndRecurrenceType {
  Never = 'Never',
  OnDate = 'OnDate',
}

export enum EndEligibilityType {
  Never = 'Never',
  OnDate = 'OnDate',
  After = 'After',
}

export enum TimeUnitType {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
}

export type ScheduleFormState = {
  startTime: string;
  startDate: Moment;
  endDate?: Moment;
  endType?: EndRecurrenceType;
  scheduleType: ScheduleType;
  projectTimeZone?: string;
  startingTimeZone?: string;
  fallbackTimeZone?: string;
  skipOnDays: Day[];
  precreateHours: string;
  recurrenceFrequency: string;
  recurrence: RecurrenceScheduleType;
};

export type ScheduleEligibilityFormState = {
  startTime?: string;
  startDate?: Moment;
  endDate?: Moment;
  endTime?: string;
  endAfterValue?: number;
  endAfterUnit: TimeUnitType;
  endType?: EndEligibilityType;
  scheduleType: ScheduleType;
  projectTimeZone: string;
  startingTimeZone?: string;
  fallbackTimeZone?: string;
};

export type CampaignStepState = {
  isOpen: boolean;
  isEditable: boolean;
  isComplete: boolean;
};

export type CampaignStepsStatesMapper = Record<CampaignStep, CampaignStepState>;

export enum CampaignEditorView {
  Edit = 'Edit',
  Summary = 'Summary',
}

export enum CampaignStep {
  Content = 'Content',
  Delivery = 'Delivery',
  ConversionAndExperiment = 'ConversionAndExperiment',
  Conversions = 'Conversions',
  Summary = 'Summary',
  Audience = 'Audience',
  AudienceTriggered = 'AudienceTriggered',
  AudienceEligibility = 'AudienceEligibility',
  AlertSettings = 'AlertSettings',
  Display = 'Display',
}

export enum ListAction {
  Send = 'Send',
  Suppress = 'Suppress',
  Eligible = 'Eligible',
}

export enum PriorityLevelDisplay {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export enum TemplateQueryString {
  Default = 'default',
  Imported = 'imported',
}

export enum TemplateEditorState {
  Open = 'open',
  Close = 'close',
}

export type TemplateState = {
  isInvalid: boolean;
  noTemplate: boolean;
  isComplete: boolean;
  isDefaultTemplate: boolean;
  hasInvalidNbaLocales: boolean;
  errorCount: number;
  templateHasContentIssues: boolean;
};
